import { CommandKind } from "../../engine/commands/CommandBuffer";
import {
  FIXED_STEP,
  MAX_SLIMES,
  SPLASH_PUSH_SCALE,
} from "../../engine/core/constants";
import type { GameContext } from "../../engine/core/GameContext";
import type { PressureState } from "../pressure/PressureState";
import { RingResolver } from "./RingResolver";

/**
 * 爆浆（[决策 0016](../../../../../docs/决策/0013-核心循环改为压力爆浆.md)）：
 * 同环同时爆开，溅出的水按表面间距传染邻居，**放大按同环个数而非链条深度**——
 * 链条收窄时威力自动回落，自限而无需封顶规则。
 */
export class BurstSystem {
  readonly rings = new RingResolver();
  /** 本步爆开的环里最宽的一个（成员数）；没有爆开时为 0。供诊断读数。 */
  lastBurstWidth = 0;
  /** 本步正在爆开的成员。它们互不传染：同环是同时爆的，不该互相喂水。 */
  private readonly exploding = new Uint8Array(MAX_SLIMES);
  /** 爆前体积快照。必须先取完再清除，否则先爆者会污染后爆者的爆前体积。 */
  private readonly burstVolume = new Float32Array(MAX_SLIMES);

  constructor(private readonly pressure: PressureState) {}

  update(context: GameContext): void {
    const { slimes, state, level, commands } = context;
    const world = level.world;
    this.lastBurstWidth = 0;
    const ringCount = this.rings.resolve(context, this.pressure);
    if (ringCount === 0) return;

    // 一、挑出窗口已到的环，记下爆前体积并标记，此时不做任何传染。
    this.exploding.fill(0);
    let readyRings = 0;
    for (let ring = 0; ring < ringCount; ring += 1) {
      if (
        (state.steps - this.rings.ringWindowStart[ring]) * FIXED_STEP <
        world.ringWindow
      )
        continue;
      readyRings += 1;
      this.lastBurstWidth = Math.max(
        this.lastBurstWidth,
        this.rings.ringSize[ring],
      );
      for (let k = 0; k < this.rings.ringSize[ring]; k += 1) {
        const id = this.rings.members[this.rings.ringOffset[ring] + k];
        this.exploding[id] = 1;
        this.burstVolume[id] = this.pressure.volume[id];
      }
    }
    if (readyRings === 0) return;

    // 二、按环宽放大并溅射。环宽是成员对环宽贡献之和（基础种每只 1），f(1) 恒为 1。
    for (let ring = 0; ring < ringCount; ring += 1) {
      if (
        (state.steps - this.rings.ringWindowStart[ring]) * FIXED_STEP <
        world.ringWindow
      )
        continue;
      const size = this.rings.ringSize[ring];
      let width = 0;
      for (let k = 0; k < size; k += 1)
        width +=
          context.catalog.slimes[
            slimes.definition[
              this.rings.members[this.rings.ringOffset[ring] + k]
            ]
          ].ringWeight;
      const amplify = Math.min(
        world.ampCap,
        1 + world.ampCoef * Math.max(0, width - 1),
      );
      for (let k = 0; k < size; k += 1) {
        this.splash(
          context,
          this.rings.members[this.rings.ringOffset[ring] + k],
          amplify,
        );
      }
    }

    // 三、统一清除。EntityDied 的来源指向把它推过临界的那一只。
    for (let id = 0; id < slimes.capacity; id += 1) {
      if (this.exploding[id] === 0) continue;
      commands.resolution.push(
        CommandKind.KillSlime,
        this.pressure.detonator[id],
        id,
        0,
        0,
        0,
        0,
      );
    }
  }

  private splash(context: GameContext, source: number, amplify: number): void {
    const { slimes, level } = context;
    const world = level.world;
    const sourceDefinition = context.catalog.slimes[slimes.definition[source]];
    const splash =
      this.burstVolume[source] *
      world.splashFraction *
      sourceDefinition.splashFraction *
      amplify;
    // 权重按**表面间距**衰减，不按圆心距离。用圆心距离时权重的绝大部分落在爆者自己
    // 身体内部——那里物理上不可能有邻居，于是贴身邻居恒定只拿到一成，链条永远传不动。
    const spread = Math.max(
      1,
      slimes.radius[source] *
        (world.splashRadius * sourceDefinition.splashRadius - 1),
    );
    const reach = slimes.radius[source] + slimes.maxActiveRadius + spread;
    const x = slimes.x[source];
    const y = slimes.y[source];

    for (let target = 0; target < slimes.capacity; target += 1) {
      if (
        slimes.active[target] === 0 ||
        this.exploding[target] !== 0 ||
        slimes.isDying(target)
      )
        continue;
      // 红线以下惰性：不吃溅射也不被推——漏掉的不能再当燃料。
      if (slimes.y[target] > world.leakLine) continue;
      const dx = slimes.x[target] - x;
      const dy = slimes.y[target] - y;
      const distance = Math.hypot(dx, dy);
      if (distance > reach) continue;
      const gap = distance - slimes.radius[source] - slimes.radius[target];
      if (gap >= spread) continue;
      const weight = 1 - Math.max(0, gap) / spread;
      // each（独立传染）：每个邻居各拿一份，邻居越多总释放越多。核心循环要奖励整形聚拢，
      // 守恒均分会让密集反而稀释，与意图冲突。
      this.pressure.volume[target] += splash * weight;
      if (this.pressure.pending[target] === 0)
        this.pressure.detonator[target] = source;
      // 炸散是连锁的天然阻尼：它让下一环变窄。
      const inverse = 1 / Math.max(1, distance);
      const targetDefinition =
        context.catalog.slimes[slimes.definition[target]];
      const push =
        (world.splashPush * weight * amplify * SPLASH_PUSH_SCALE) /
        (targetDefinition.mass *
          Math.max(
            0.5,
            this.pressure.volume[target] / targetDefinition.burstVolume + 0.4,
          ));
      slimes.vx[target] += dx * inverse * push;
      slimes.vy[target] += dy * inverse * push;
    }
  }
}
